import React, { useState } from 'react';
import { MantineProvider, ActionIcon } from '@mantine/core';
import { spotlight } from '@mantine/spotlight';
import { Search } from 'tabler-icons-react';
import { Link } from '@inertiajs/react';
import { useUser } from '../../src/context/UserContext';

// Component imports
import Sidebar from '../Sidebar';
import SpotlightSearch from './SpotlightSearch';
import SearchModal from './SearchModal';
import UserSection from './UserSection';

// Hook import
import { useSearch } from './hooks/useSearch';

// Asset imports
import logoUrl from '/assets/NEW WCWD LOGO WHITE.svg';
import mobileLogoUrl from '/assets/Logomark_orange.svg';
import hamburgerIcon from '/assets/white-hamburger-menu.png';

// Styles
import '../../styles/Navbar.css';
import '@mantine/spotlight/styles.css';

interface NavbarProps {
  toggleSidebar: () => void;
  isLoginPage?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar }) => {
  const { user, logout } = useUser();
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  // Search functionality via custom hook
  const {
    searchQuery,
    spotlightActions,
    showFullResults,
    fullSearchResults,
    fullSearchLoading,
    handleSearchChange,
    handleBackToSearch,
    closeFullResults,
  } = useSearch();

  const handleSidebarToggle = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const handleSearchIconClick = () => {
    spotlight.open();
  };

  return (
    <MantineProvider>
      <div>
        {/* Sidebar */}
        <Sidebar isOpen={isSidebarOpen} closeSidebar={() => setSidebarOpen(false)} user={user} />

        {/* Spotlight Search */}
        <SpotlightSearch actions={spotlightActions} onSearchChange={handleSearchChange} />

        {/* Full Search Results Modal */}
        <SearchModal
          opened={showFullResults}
          onClose={closeFullResults}
          searchQuery={searchQuery}
          fullSearchResults={fullSearchResults}
          isLoading={fullSearchLoading}
          onBackToSearch={handleBackToSearch}
        />

        {/* Main Navigation */}
        <nav className="navbar">
          <div className="navbar-content">
            {/* Hamburger Menu */}
            <div className="hamburger-icon order-first">
              <button onClick={handleSidebarToggle} className="p-2 bg-transparent border-none" aria-label="Toggle menu">
                <img src={hamburgerIcon} alt="Menu" className="w-6 h-6" />
              </button>
            </div>

            {/* Desktop Logo */}
            <div className="desktop-logo">
              <Link href="/">
                <img src={logoUrl} alt="Logo" className="site-nav-logo" />
              </Link>
            </div>

            {/* Mobile Logo */}
            <div className="mobile-logo">
              <Link href="/" className="navbar-brand">
                <img src={mobileLogoUrl} alt="Logo" className="site-nav-logo" style={{ width: '45px' }} />
              </Link>
            </div>

            {/* Search Icon */}
            <div className="search-icon">
              <ActionIcon
                size="xl"
                color="gray"
                variant="light"
                onClick={handleSearchIconClick}
                className="search-trigger"
                title="Search (Ctrl+K)"
                aria-label="Open search"
              >
                <Search strokeWidth={1.75} color="gray" style={{ width: '24px', height: '24px' }} />
              </ActionIcon>
            </div>

            {/* User Section */}
            <div className="flex items-center space-x-4 ml-auto mr-2">
              <UserSection user={user} onLogout={logout} />
            </div>
          </div>
        </nav>
      </div>
    </MantineProvider>
  );
};

export default Navbar;
